import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import config from '../config/environment';
import {
  Mail,
  Shield,
  UserPlus,
  Crown,
  Eye,
  Edit,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';

interface InvitationDetails {
  _id: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  projectId: {
    _id: string;
    name: string;
    description?: string;
  };
  invitedBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  isNewUser?: boolean;
}

const InvitationAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      fetchInvitationDetails();
    }
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      setError(null);

      // === DIAGNOSTIC: Log token and URL ===
      console.log('[INVITATION] Token from URL:', token);
      console.log('[INVITATION] Token length:', token?.length);
      console.log('[INVITATION] Token type:', typeof token);

      const apiUrl = `${config.apiUrl}/invitations/${token}`;
      console.log('[INVITATION] Full API URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('[INVITATION] Response status:', response.status);
      console.log('[INVITATION] Response content-type:', response.headers.get('content-type'));

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Erro ao carregar convite');
        } else {
          // Response is HTML or other format
          const textResponse = await response.text();
          console.error('[INVITATION] Non-JSON response:', textResponse.substring(0, 200));
          throw new Error(`Erro ao carregar convite (HTTP ${response.status}). Verifique se o link está correto.`);
        }
      }

      const data = await response.json();

      // Merge invitation data with requiresRegistration flag
      const invitationData = {
        ...data.data.invitation,
        isNewUser: data.data.requiresRegistration
      };

      setInvitation(invitationData);

      // Se é um novo usuário, mostrar formulário de senha
      if (data.data.requiresRegistration) {
        setShowPasswordForm(true);
      }
    } catch (error: any) {
      console.error('Error fetching invitation:', error);
      setError(error.message || 'Erro ao carregar convite. Verifique se o link está correto.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!invitation) return;

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${config.apiUrl}/invitations/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(showPasswordForm ? formData : {})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao aceitar convite');
      }

      const data = await response.json();

      // Atualizar o status do convite para 'accepted'
      if (invitation) {
        setInvitation({
          ...invitation,
          status: 'accepted'
        });
      }

      setSuccess(true);

      // Se recebeu token de auth, salvar no localStorage
      if (data.data.token) {
        localStorage.setItem('token', data.data.token);
      }

      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/projects');
      }, 3000);
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      setError(error.message || 'Erro ao aceitar convite. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectInvitation = async () => {
    if (!invitation) return;

    if (!window.confirm('Tem certeza que deseja rejeitar este convite?')) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${config.apiUrl}/invitations/${token}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao rejeitar convite');
      }

      // Atualizar o status do convite para 'rejected'
      if (invitation) {
        setInvitation({
          ...invitation,
          status: 'rejected'
        });
      }

      // Mostrar mensagem de sucesso
      setSuccess(true);

      // Se o usuário já tem conta, redirecionar para login após 3 segundos
      // Se é novo usuário, não precisa fazer nada (ele não tem conta mesmo)
      if (!invitation.isNewUser) {
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error rejecting invitation:', error);
      setError(error.message || 'Erro ao rejeitar convite. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'member':
        return <Edit className="w-4 h-4 text-green-500" />;
      case 'viewer':
        return <Eye className="w-4 h-4 text-gray-500" />;
      default:
        return <UserPlus className="w-4 h-4" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'member':
        return 'Membro';
      case 'viewer':
        return 'Visualizador';
      default:
        return role;
    }
  };

  const isFormValid = () => {
    if (!showPasswordForm) return true;
    
    return formData.name.trim() && 
           formData.password.length >= 6 && 
           formData.password === formData.confirmPassword;
  };

  const isExpired = invitation && new Date(invitation.expiresAt) < new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <LoadingSpinner message="Carregando convite" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground-primary mb-2">Erro ao carregar convite</h2>
            <p className="text-foreground-secondary mb-4">{error}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    const isAccepted = invitation?.status === 'accepted';
    const isNewUser = invitation?.isNewUser;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground-primary mb-2">
              {isAccepted ?
                (isNewUser ? 'Conta criada e convite aceito!' : 'Convite aceito com sucesso!') :
                'Convite rejeitado'
              }
            </h2>
            <p className="text-foreground-secondary mb-4">
              {isAccepted ?
                (isNewUser ?
                  'Sua conta foi criada e você já está participando do projeto. Redirecionando...' :
                  'Você será redirecionado para o projeto em alguns segundos...'
                ) :
                (isNewUser ?
                  'Tudo bem! Você não será cadastrado no sistema.' :
                  'Você será redirecionado para o login em alguns segundos...'
                )
              }
            </p>
            {(isAccepted || !isNewUser) && (
              <Loader2 className="w-4 h-4 animate-spin mx-auto text-blue-500" />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-400" />
          </div>
          <CardTitle className="text-xl">Convite para Projeto</CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {invitation && (
            <>
              {/* Invitation Details */}
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-sm text-foreground-tertiary">Projeto:</p>
                  <p className="font-medium text-foreground-primary">{invitation.projectId.name}</p>
                  {invitation.projectId.description && (
                    <p className="text-sm text-foreground-secondary">{invitation.projectId.description}</p>
                  )}
                </div>
                
                <div>
                  <p className="text-sm text-foreground-tertiary">Convidado por:</p>
                  <p className="font-medium text-foreground-primary">{invitation.invitedBy.name}</p>
                  <p className="text-sm text-foreground-secondary">{invitation.invitedBy.email}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <p className="text-sm text-foreground-tertiary">Seu papel:</p>
                  {getRoleIcon(invitation.role)}
                  <span className="font-medium text-foreground-primary">{getRoleLabel(invitation.role)}</span>
                </div>
                
                <div>
                  <p className="text-sm text-foreground-tertiary">Email:</p>
                  <p className="font-medium text-foreground-primary">{invitation.email}</p>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* Expired Message */}
              {isExpired && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-yellow-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Este convite expirou</span>
                  </div>
                </div>
              )}

              {/* Password Form for New Users */}
              {showPasswordForm && !isExpired && (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-foreground-secondary">
                      Como é seu primeiro acesso, você precisa criar uma senha
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome completo</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Digite seu nome completo"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Senha</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Digite sua senha (mínimo 6 caracteres)"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar senha</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirme sua senha"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-sm text-red-400">As senhas não coincidem</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {!isExpired && (
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleRejectInvitation}
                    loading={processing}
                    loadingText="Rejeitando..."
                    className="flex-1"
                  >
                    Rejeitar
                  </Button>
                  <Button
                    variant="gradient"
                    onClick={handleAcceptInvitation}
                    loading={processing}
                    loadingText="Aceitando..."
                    disabled={!isFormValid()}
                    className="flex-1"
                  >
                    Aceitar
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationAccept;